import { useCallback, useEffect, useMemo, useState } from "react";
import {
  ApiError,
  deleteUserEvents,
  fetchAiStatus,
  fetchEvents,
  fetchMe,
  fetchSettings,
  saveMinutesPerImage,
  type AiStatus,
  type GetIdToken,
  type StatsEvent,
  type StatsSettings,
} from "./api";
import {
  PERIOD_LABELS,
  SOURCE_LABELS,
  byMonth,
  byPerson,
  filterByPeriod,
  formatDateTime,
  formatMinutes,
  formatMonth,
  toCsv,
  toMarkdown,
  totals,
  type Period,
} from "./aggregate";
import { AiCard, AiWarnings } from "./AiCard";

type Load =
  | { status: "loading" }
  | { status: "denied" }
  | { status: "error"; message: string }
  | { status: "ready"; events: StatsEvent[]; settings: StatsSettings; ai: AiStatus | null };

const HISTORY_STEP = 50;

export function AdminDashboard({ getIdToken }: { getIdToken: GetIdToken }) {
  const [load, setLoad] = useState<Load>({ status: "loading" });
  const [period, setPeriod] = useState<Period>("month");
  const [historyLimit, setHistoryLimit] = useState(HISTORY_STEP);

  const reload = useCallback(async () => {
    try {
      const me = await fetchMe(getIdToken);
      if (!me.isAdmin) return setLoad({ status: "denied" });
      const [events, settings, ai] = await Promise.all([
        fetchEvents(getIdToken),
        fetchSettings(getIdToken),
        fetchAiStatus(getIdToken).catch(() => null),
      ]);
      setLoad({ status: "ready", events, settings, ai });
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) return setLoad({ status: "denied" });
      setLoad({ status: "error", message: err instanceof Error ? err.message : String(err) });
    }
    // getIdToken ist über die Sitzung stabil.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  return (
    <div className="max-w-5xl mx-auto px-6 py-8 space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <a href="/" className="text-sm text-kampmeyer-neutral-500 hover:underline">
            ← Zur Bildskalierung
          </a>
          <h1 className="text-2xl font-semibold mt-1">Nutzungsstatistik</h1>
        </div>
        {load.status === "ready" && (
          <PeriodSwitch value={period} onChange={(p) => (setPeriod(p), setHistoryLimit(HISTORY_STEP))} />
        )}
      </div>

      {load.status === "loading" && <div className="card text-sm text-kampmeyer-neutral-500">Lade Statistik…</div>}
      {load.status === "denied" && (
        <div className="card">
          <h2 className="text-lg font-semibold mb-1">Kein Zugriff</h2>
          <p className="text-sm text-kampmeyer-neutral-500">
            Die Statistik ist nur für Administratorinnen und Administratoren freigeschaltet.
          </p>
        </div>
      )}
      {load.status === "error" && (
        <div className="card">
          <h2 className="text-lg font-semibold mb-1">Statistik konnte nicht geladen werden</h2>
          <p className="text-sm text-kampmeyer-neutral-500 mb-4">{load.message}</p>
          <button className="btn-secondary" onClick={() => void reload()}>
            Erneut versuchen
          </button>
        </div>
      )}
      {load.status === "ready" && (
        <Dashboard
          ai={load.ai}
          events={load.events}
          settings={load.settings}
          period={period}
          historyLimit={historyLimit}
          onMoreHistory={() => setHistoryLimit((n) => n + HISTORY_STEP)}
          getIdToken={getIdToken}
          onSettings={(settings) => setLoad({ ...load, settings })}
          onReload={reload}
        />
      )}
    </div>
  );
}

function PeriodSwitch({ value, onChange }: { value: Period; onChange: (p: Period) => void }) {
  return (
    <div className="inline-flex rounded-md border border-kampmeyer-neutral-300 overflow-hidden text-sm" role="group">
      {(Object.keys(PERIOD_LABELS) as Period[]).map((p) => (
        <button
          key={p}
          onClick={() => onChange(p)}
          aria-pressed={value === p}
          className={`px-3 py-1.5 transition ${
            value === p ? "bg-kampmeyer-primary text-white" : "hover:bg-kampmeyer-neutral-100"
          }`}
        >
          {PERIOD_LABELS[p]}
        </button>
      ))}
    </div>
  );
}

interface DashboardProps {
  ai: AiStatus | null;
  events: StatsEvent[];
  settings: StatsSettings;
  period: Period;
  historyLimit: number;
  onMoreHistory: () => void;
  getIdToken: GetIdToken;
  onSettings: (s: StatsSettings) => void;
  onReload: () => Promise<void>;
}

function Dashboard({ ai, events, settings, period, historyLimit, onMoreHistory, getIdToken, onSettings, onReload }: DashboardProps) {
  const factor = settings.minutesPerImage;
  const visible = useMemo(() => filterByPeriod(events, period), [events, period]);
  const t = totals(visible, factor);
  const people = byPerson(visible, factor);
  const months = byMonth(visible, factor);

  const download = (name: string, content: string, type: string) => {
    const url = URL.createObjectURL(new Blob([content], { type }));
    const a = document.createElement("a");
    a.href = url;
    a.download = name;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };
  const stamp = new Date().toISOString().slice(0, 10);

  const removePerson = async (email: string, name: string) => {
    const ok = window.confirm(
      `Alle Statistik-Einträge von ${name || email} endgültig löschen?\n\nDas betrifft alle Zeiträume und kann nicht rückgängig gemacht werden.`,
    );
    if (!ok) return;
    try {
      const { deleted } = await deleteUserEvents(getIdToken, email);
      window.alert(`${deleted} Einträge gelöscht.`);
      await onReload();
    } catch (err) {
      window.alert(`Löschen fehlgeschlagen: ${err instanceof Error ? err.message : err}`);
    }
  };

  return (
    <>
      <AiWarnings status={ai} />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Kpi label="Skalierte Bilder" value={t.images.toLocaleString("de-DE")} />
        <Kpi label="Eingesparte Zeit" value={formatMinutes(t.minutesSaved)} hint={`${String(factor).replace(".", ",")} Min. pro Bild`} />
        <Kpi label="Vorgänge" value={t.runs.toLocaleString("de-DE")} />
        <Kpi label="Aktive Personen" value={String(t.people)} />
      </div>

      <FactorCard settings={settings} getIdToken={getIdToken} onSaved={onSettings} />

      {ai && <AiCard status={ai} getIdToken={getIdToken} onChanged={() => void onReload()} />}

      {events.length === 0 ? (
        <div className="card text-sm text-kampmeyer-neutral-500">
          Noch keine Einträge. Jeder Skalier-Vorgang angemeldeter Personen wird ab jetzt automatisch erfasst.
        </div>
      ) : (
        <>
          <div className="card">
            <div className="flex flex-wrap items-baseline justify-between gap-3 mb-3">
              <h2 className="text-lg font-semibold">Pro Person</h2>
              <div className="flex gap-2">
                <button
                  className="btn-secondary !px-3 !py-1.5 text-sm"
                  onClick={() => download(`bildskalierung-statistik-${stamp}.csv`, toCsv(visible, factor), "text/csv;charset=utf-8")}
                >
                  Export CSV
                </button>
                <button
                  className="btn-secondary !px-3 !py-1.5 text-sm"
                  onClick={() =>
                    download(
                      `bildskalierung-statistik-${stamp}.md`,
                      toMarkdown(visible, factor, PERIOD_LABELS[period]),
                      "text/markdown;charset=utf-8",
                    )
                  }
                >
                  Export Markdown
                </button>
              </div>
            </div>
            {people.length === 0 ? (
              <Empty />
            ) : (
              <Table head={["Person", "Vorgänge", "Bilder", "Eingesparte Zeit", "Zuletzt aktiv", ""]} alignRightFrom={1}>
                {people.map((p) => (
                  <tr key={p.email} className="border-t border-kampmeyer-neutral-100">
                    <td className="py-2 pr-3">
                      <div className="font-medium">{p.name || p.email}</div>
                      {p.name && <div className="text-xs text-kampmeyer-neutral-500">{p.email}</div>}
                    </td>
                    <td className="py-2 px-3 text-right tabular-nums">{p.runs}</td>
                    <td className="py-2 px-3 text-right tabular-nums">{p.images.toLocaleString("de-DE")}</td>
                    <td className="py-2 px-3 text-right tabular-nums">{formatMinutes(p.minutesSaved)}</td>
                    <td className="py-2 px-3 text-right tabular-nums whitespace-nowrap">{formatDateTime(p.lastTs)}</td>
                    <td className="py-2 pl-3 text-right">
                      <button
                        onClick={() => void removePerson(p.email, p.name)}
                        className="text-xs text-red-700 hover:underline whitespace-nowrap"
                        title="Alle Einträge dieser Person löschen (z. B. bei Austritt oder Löschanfrage)"
                      >
                        Daten löschen
                      </button>
                    </td>
                  </tr>
                ))}
              </Table>
            )}
          </div>

          <div className="card">
            <h2 className="text-lg font-semibold mb-3">Pro Monat</h2>
            {months.length === 0 ? (
              <Empty />
            ) : (
              <Table head={["Monat", "Vorgänge", "Bilder", "Eingesparte Zeit", "Personen"]} alignRightFrom={1}>
                {months.map((m) => (
                  <tr key={m.month} className="border-t border-kampmeyer-neutral-100">
                    <td className="py-2 pr-3">{formatMonth(m.month)}</td>
                    <td className="py-2 px-3 text-right tabular-nums">{m.runs}</td>
                    <td className="py-2 px-3 text-right tabular-nums">{m.images.toLocaleString("de-DE")}</td>
                    <td className="py-2 px-3 text-right tabular-nums">{formatMinutes(m.minutesSaved)}</td>
                    <td className="py-2 pl-3 text-right tabular-nums">{m.people}</td>
                  </tr>
                ))}
              </Table>
            )}
          </div>

          <div className="card">
            <h2 className="text-lg font-semibold mb-3">Verlauf der Objekte</h2>
            {visible.length === 0 ? (
              <Empty />
            ) : (
              <>
                <Table head={["Datum", "Person", "Objekt", "Quelle", "Bilder"]} alignRightFrom={4}>
                  {visible.slice(0, historyLimit).map((e) => (
                    <tr key={e.id} className="border-t border-kampmeyer-neutral-100">
                      <td className="py-2 pr-3 whitespace-nowrap tabular-nums">{formatDateTime(e.ts)}</td>
                      <td className="py-2 px-3">{e.userName || e.userEmail}</td>
                      <td className="py-2 px-3 break-all">{e.objectName || "–"}</td>
                      <td className="py-2 px-3 whitespace-nowrap">{SOURCE_LABELS[e.sourceType]}</td>
                      <td className="py-2 pl-3 text-right tabular-nums">
                        {e.images}
                        {e.titleImages > 0 && (
                          <span className="text-xs text-kampmeyer-neutral-500"> (inkl. {e.titleImages} Titel)</span>
                        )}
                        {e.errors > 0 && <span className="text-xs text-red-700"> · {e.errors} Fehler</span>}
                      </td>
                    </tr>
                  ))}
                </Table>
                {visible.length > historyLimit && (
                  <button className="btn-secondary mt-4 !px-3 !py-1.5 text-sm" onClick={onMoreHistory}>
                    Weitere anzeigen ({visible.length - historyLimit})
                  </button>
                )}
              </>
            )}
          </div>
        </>
      )}
    </>
  );
}

function FactorCard({
  settings,
  getIdToken,
  onSaved,
}: {
  settings: StatsSettings;
  getIdToken: GetIdToken;
  onSaved: (s: StatsSettings) => void;
}) {
  const [value, setValue] = useState(String(settings.minutesPerImage).replace(".", ","));
  const [state, setState] = useState<"idle" | "saving" | "saved" | string>("idle");
  const parsed = Number(value.replace(",", "."));
  const valid = value.trim() !== "" && Number.isFinite(parsed) && parsed >= 0 && parsed <= 120;
  const changed = valid && parsed !== settings.minutesPerImage;

  const save = async () => {
    setState("saving");
    try {
      onSaved(await saveMinutesPerImage(getIdToken, parsed));
      setState("saved");
    } catch (err) {
      setState(err instanceof Error ? err.message : String(err));
    }
  };

  return (
    <div className="card">
      <h2 className="text-lg font-semibold mb-1">Berechnung der eingesparten Zeit</h2>
      <p className="text-sm text-kampmeyer-neutral-500 mb-3">
        Eingesparte Zeit = Anzahl Bilder × Minuten, die ein Bild früher von Hand gedauert hat. Eine Änderung gilt sofort für
        alle Zeiträume, auch rückwirkend.
      </p>
      <form
        className="flex flex-wrap items-center gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          if (changed) void save();
        }}
      >
        <label className="text-sm font-medium" htmlFor="minutes-per-image">
          Minuten pro Bild
        </label>
        <input
          id="minutes-per-image"
          inputMode="decimal"
          value={value}
          onChange={(e) => {
            setValue(e.target.value);
            setState("idle");
          }}
          className="w-24 px-3 py-2 text-sm rounded border border-kampmeyer-neutral-300 bg-white text-kampmeyer-neutral-900 focus:outline-none focus:ring-2 focus:ring-kampmeyer-primary/40"
        />
        <button type="submit" className="btn-primary !px-4 !py-2 text-sm" disabled={!changed || state === "saving"}>
          {state === "saving" ? "Speichere…" : "Speichern"}
        </button>
        {state === "saved" && <span className="text-sm text-green-700">✓ Gespeichert</span>}
        {!valid && <span className="text-sm text-red-700">Bitte eine Zahl zwischen 0 und 120 eingeben (z. B. 0,5).</span>}
        {state !== "idle" && state !== "saving" && state !== "saved" && <span className="text-sm text-red-700">{state}</span>}
      </form>
      {settings.updatedBy && (
        <p className="text-xs text-kampmeyer-neutral-500 mt-2">
          Zuletzt geändert von {settings.updatedBy} am {formatDateTime(settings.updatedAt)}
        </p>
      )}
    </div>
  );
}

function Kpi({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="card !p-4">
      <div className="text-xs text-kampmeyer-neutral-500">{label}</div>
      <div className="text-2xl font-semibold mt-1 tabular-nums">{value}</div>
      {hint && <div className="text-xs text-kampmeyer-neutral-500 mt-0.5">{hint}</div>}
    </div>
  );
}

function Table({ head, alignRightFrom, children }: { head: string[]; alignRightFrom: number; children: React.ReactNode }) {
  return (
    <div className="overflow-x-auto -mx-2 px-2">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-xs text-kampmeyer-neutral-500">
            {head.map((h, i) => (
              <th key={i} className={`font-medium pb-2 ${i === 0 ? "pr-3" : "px-3"} ${i >= alignRightFrom ? "text-right" : "text-left"}`}>
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

function Empty() {
  return <p className="text-sm text-kampmeyer-neutral-500">Keine Einträge in diesem Zeitraum.</p>;
}
